import { useState, useCallback, useEffect } from "react";

/**
 * Custom hook for managing chart navigation state
 *
 * @param {number} initialMin - Initial minimum visible index
 * @param {number} initialMax - Initial maximum visible index
 * @param {Array} items - Array of chart items
 * @returns {Object} Navigation state and handlers
 */
const useChartNavigation = (initialMin = 0, initialMax = 5, items = []) => {
  const [range, setRange] = useState({ min: initialMin, max: initialMax });

  // Reset range when items length changes (not the array reference)
  useEffect(() => {
    setRange({ min: initialMin, max: initialMax });
  }, [items.length, initialMin, initialMax]);

  const navigateLeft = useCallback(() => {
    setRange((prev) => ({
      min: Math.max(0, prev.min - 1),
      max: Math.max(initialMax, prev.max - 1),
    }));
  }, [initialMax]);

  const navigateRight = useCallback(() => {
    setRange((prev) => ({
      min: Math.min(items.length - (initialMax - initialMin), prev.min + 1),
      max: Math.min(items.length, prev.max + 1),
    }));
  }, [items.length, initialMax, initialMin]);

  const resetRange = useCallback(() => {
    setRange({ min: initialMin, max: initialMax });
  }, [initialMin, initialMax]);

  // Aliases for Y-axis navigation (more semantic naming)
  const navigateUp = navigateRight;
  const navigateDown = navigateLeft;

  return {
    range,
    setRange,
    navigateLeft,
    navigateRight,
    navigateUp,
    navigateDown,
    resetRange,
  };
};

export default useChartNavigation;
